/**
 * 💰 YACHT DESTINATION PRICING SERVICE
 * Manages yacht destination pricing for one-way and round-trip bookings
 */

import apiClient from './apiClient';

export interface YachtDestinationPricing {
  id: string;
  yachtId: string;
  ownerId: string;
  destinationId: string;
  oneWayPrice: number; // In Naira (5000 = ₦5,000.00)
  roundTripPrice: number; // In Naira
  estimatedDurationMinutes: number;
  isAvailable: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Populated fields
  destination?: {
    destinationName: string;
    country: string;
    city: string;
    description?: string;
    estimatedDistanceKm?: number;
  };
}

export interface YachtWithPricing {
  yachtId: string;
  yachtName: string;
  yachtType: string;
  capacity: number;
  ownerId: string;
  ownerName?: string;
  city?: string;
  country?: string;
  homeport?: string;
  destinationPricingCount: number;
  hasDestinationPricing: boolean;
  pricings?: YachtDestinationPricing[];
}

export interface SetYachtPricingData {
  destinationId: string;
  oneWayPrice: number;
  roundTripPrice: number;
  estimatedDurationMinutes: number;
  isAvailable?: boolean;
  notes?: string;
}

export interface UpdateYachtPricingData {
  oneWayPrice?: number;
  roundTripPrice?: number;
  estimatedDurationMinutes?: number;
  isAvailable?: boolean;
  notes?: string;
}

export interface YachtPricingStats {
  totalYachts: number;
  yachtsWithPricing: number;
  totalPricingRecords: number;
  avgOneWayPrice: number;
  avgRoundTripPrice: number;
  byDestination: {
    destinationName: string;
    country: string;
    city: string;
    yachtCount: number;
  }[];
}

export interface YachtPricingResponse {
  success: boolean;
  data?: YachtDestinationPricing;
  pricings?: YachtDestinationPricing[];
  yachts?: YachtWithPricing[];
  stats?: YachtPricingStats;
  error?: string;
  message?: string;
}

export const yachtDestinationPricingService = {
  /**
   * 📥 Get all yachts with their destination pricing status
   */
  async getYachtsWithPricing(filters?: {
    country?: string;
    city?: string;
    destinationId?: string;
    hasPricing?: boolean;
  }): Promise<YachtPricingResponse> {
    try {
      const params: any = {};
      if (filters?.country) params.country = filters.country;
      if (filters?.city) params.city = filters.city;
      if (filters?.destinationId) params.destinationId = filters.destinationId;
      if (filters?.hasPricing !== undefined) params.hasPricing = filters.hasPricing;

      const response = await apiClient.get('/api/v1/admin/yachts/destination-pricing', { params });


      if (response.data.success) {
        const yachts = response.data.yachts || response.data.data?.yachts || response.data.data || [];
        return {
          success: true,
          yachts: Array.isArray(yachts) ? yachts : []
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch yachts'
      };
    } catch (error: any) {
      console.error('❌ Error fetching yachts with pricing:', error);
      console.error('❌ Error details:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch yachts'
      };
    }
  },

  /**
   * 📥 Get pricing for a specific yacht
   */
  async getYachtPricing(yachtId: string): Promise<YachtPricingResponse> {
    try {
      const response = await apiClient.get(`/api/v1/admin/yachts/${yachtId}/destinations`);

      if (response.data.success) {
        const result = response.data;

        // Handle different backend response formats
        let pricings = result.pricings || result.data?.pricing || result.data?.pricings || result.data?.destinations || [];

        // Ensure we have an array
        if (!Array.isArray(pricings)) {
          pricings = [];
        }

        // Transform backend format to frontend format
        const transformedPricings = pricings.map((pricing: any) => ({
          id: pricing.id,
          yachtId: pricing.yachtId || result.data?.yachtId,
          ownerId: pricing.ownerId,
          destinationId: pricing.destinationId || pricing.id,
          // Extract prices from nested pricing object
          oneWayPrice: pricing.pricing?.oneWayPrice || pricing.oneWayPrice || 0,
          roundTripPrice: pricing.pricing?.roundTripPrice || pricing.roundTripPrice || 0,
          estimatedDurationMinutes: pricing.estimatedDurationMinutes || 0,
          isAvailable: pricing.isActive !== undefined ? pricing.isActive : (pricing.isAvailable !== undefined ? pricing.isAvailable : true),
          notes: pricing.notes,
          createdAt: pricing.createdAt,
          updatedAt: pricing.updatedAt,
          destination: pricing.destination ? {
            destinationName: pricing.destination.name || pricing.destination.destinationName,
            country: pricing.destination.country,
            city: pricing.destination.city,
            description: pricing.destination.description,
            estimatedDistanceKm: pricing.destination.location?.estimatedDistanceKm
          } : {
            destinationName: pricing.destinationName,
            country: pricing.country,
            city: pricing.city,
            description: pricing.description,
            estimatedDistanceKm: pricing.estimatedDistanceKm || pricing.location?.estimatedDistanceKm
          }
        }));

        return {
          success: true,
          pricings: transformedPricings
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch yacht pricing'
      };
    } catch (error: any) {
      console.error('❌ Error fetching yacht pricing:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch yacht pricing'
      };
    }
  },

  /**
   * ➕ Set yacht destination pricing for a yacht
   */
  async setPricing(yachtId: string, data: SetYachtPricingData): Promise<YachtPricingResponse> {
    try {
      const response = await apiClient.post(`/api/v1/admin/yachts/${yachtId}/destinations`, data);

      if (response.data.success) {
        const pricing = response.data.data || response.data.pricing || response.data;
        return {
          success: true,
          data: pricing,
          message: response.data.message || 'Pricing set successfully'
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to set pricing'
      };
    } catch (error: any) {
      console.error('❌ Error setting pricing:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to set pricing'
      };
    }
  },

  /**
   * ✏️ Update yacht destination pricing
   */
  async updatePricing(pricingId: string, data: UpdateYachtPricingData): Promise<YachtPricingResponse> {
    try {
      const response = await apiClient.patch(`/api/v1/admin/yacht-destination-pricing/${pricingId}`, data);

      if (response.data.success) {
        const pricing = response.data.data || response.data.pricing || response.data;
        return {
          success: true,
          data: pricing,
          message: response.data.message || 'Pricing updated successfully'
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to update pricing'
      };
    } catch (error: any) {
      console.error('❌ Error updating pricing:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to update pricing'
      };
    }
  },

  /**
   * 🗑️ Delete yacht destination pricing
   */
  async deletePricing(pricingId: string): Promise<YachtPricingResponse> {
    try {
      const response = await apiClient.delete(`/api/v1/admin/yacht-destination-pricing/${pricingId}`);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Pricing deleted successfully'
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to delete pricing'
      };
    } catch (error: any) {
      console.error('❌ Error deleting pricing:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to delete pricing'
      };
    }
  },

  /**
   * 🚫 Deactivate yacht destination pricing (soft delete)
   */
  async deactivatePricing(pricingId: string): Promise<YachtPricingResponse> {
    return this.updatePricing(pricingId, { isAvailable: false });
  },

  /**
   * 📊 Get pricing statistics
   */
  async getPricingStats(): Promise<YachtPricingResponse> {
    try {
      const response = await apiClient.get('/api/v1/admin/yacht-destination-pricing/stats');

      if (response.data.success) {
        const stats = response.data.stats || response.data.data?.stats || response.data.data;
        return {
          success: true,
          stats: stats
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch pricing stats'
      };
    } catch (error: any) {
      console.error('❌ Error fetching pricing stats:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch pricing stats'
      };
    }
  },
};
